import { NavLink, Link } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  BarChart3,
  MessageSquare,
  Video,
  GraduationCap,
  AlertCircle,
  BookOpen,
  Settings2,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useBrand } from "@/contexts/TenantConfigContext";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  SidebarHeader,
  useSidebar,
} from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";

const menuItems = [
  { title: "ダッシュボード", url: "/dashboard", icon: LayoutDashboard },
  { title: "受講生管理", url: "/students", icon: Users },
  { title: "コース管理", url: "/courses", icon: GraduationCap },
  { title: "分析", url: "/analytics", icon: BarChart3 },
  { title: "AIチャット", url: "/chat", icon: MessageSquare },
  { title: "動画管理", url: "/videos", icon: Video },
  { title: "エスカレーション", url: "/escalations", icon: AlertCircle },
  { title: "ヘルプ", url: "/docs", icon: BookOpen },
];

// Admin-only menu items (admin, owner, super_admin)
const adminMenuItems = [
  { title: "OEM設定", url: "/oem-wizard", icon: Settings2 },
];

export function AppSidebar() {
  const { state } = useSidebar();
  const { user } = useAuth();
  const brand = useBrand();

  // Debug: log brand data to identify why logo isn't showing
  console.log("[AppSidebar] brand:", brand);

  const isCollapsed = state === "collapsed";
  const isAdmin = user?.role === "admin" || user?.role === "owner" || user?.role === "super_admin";

  return (
    <Sidebar
      className={isCollapsed ? "w-14" : "w-60"}
      collapsible="icon"
      role="navigation"
      aria-label="メインナビゲーション"
    >
      {/* Logo Header */}
      <SidebarHeader className="p-4">
        <Link to="/dashboard" className="flex items-center gap-2">
          {brand.logo_url ? (
            <img
              src={brand.logo_url}
              alt={`${brand.name} ロゴ`}
              className={isCollapsed ? "h-8 w-8 object-contain" : "h-10 max-w-[180px] object-contain"}
            />
          ) : (
            <span className={isCollapsed ? "sr-only" : "font-bold text-lg truncate"}>
              {brand.name}
            </span>
          )}
        </Link>
      </SidebarHeader>

      <Separator className="mx-2" />

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel className={isCollapsed ? "sr-only" : ""}>
            学習管理システム
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu role="menubar" aria-label="メニュー">
              {menuItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={item.url}
                      className={({ isActive }) =>
                        isActive
                          ? "bg-muted text-primary font-medium"
                          : "hover:bg-muted/50"
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <item.icon className="h-4 w-4" aria-hidden="true" />
                          {!isCollapsed && <span>{item.title}</span>}
                          {isActive && <span className="sr-only">（現在のページ）</span>}
                        </>
                      )}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Admin-only section */}
        {isAdmin && (
          <SidebarGroup>
            <SidebarGroupLabel className={isCollapsed ? "sr-only" : ""}>
              管理者設定
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu role="menubar" aria-label="管理者メニュー">
                {adminMenuItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild>
                      <NavLink
                        to={item.url}
                        className={({ isActive }) =>
                          isActive
                            ? "bg-muted text-primary font-medium"
                            : "hover:bg-muted/50"
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <item.icon className="h-4 w-4" aria-hidden="true" />
                            {!isCollapsed && <span>{item.title}</span>}
                            {isActive && <span className="sr-only">（現在のページ）</span>}
                          </>
                        )}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter>
        {!isCollapsed && (
          <div className="p-2 space-y-2">
            <Separator />
            <div className="flex justify-center gap-4 text-xs text-muted-foreground">
              <Link to="/terms" className="hover:text-primary underline">
                利用規約
              </Link>
              <Link to="/privacy" className="hover:text-primary underline">
                プライバシー
              </Link>
            </div>
          </div>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
